import {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  WheelEventHandler,
} from 'react';
import { useLocation } from 'react-router-dom';

import {
  listenToWebsocket,
  stopListenFromWebsocket,
  WebsocketCallback,
} from '#/api/services/webSocketApiService';
import { WebsocketSubscriptionType } from '#/api/services/websocketTypes';
// import { useSelector } from '#/reduxState/store';
import { getCurrentStateChainConfig } from '#/config/configUtils';
import { getActiveChainFromState } from '#/reduxState/exchange/exchangeActiveChain';
import {
  chainsMap,
  getChainNativeAssetTitle,
  isChainListed,
} from '#/reduxState/exchange/exchangeActiveChainUtils';

/**
 * Use interval hook.
 * To use interval only when window/tab is active use `useActiveWindowInterval`
 *
 * @param delayMs interval time in milliseconds
 * @see https://overreacted.io/making-setinterval-declarative-with-react-hooks/
 */
export function useInterval(callback: () => void, delayMs: number | null) {
  const savedCallback = useRef<() => void>();

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    if (delayMs && delayMs < 10) {
      console.error('PerfError: Trying to useInterval smaller than 10ms');
      return;
    }
    function tick() {
      if (savedCallback && savedCallback.current) {
        savedCallback.current();
      }
    }
    if (delayMs !== null) {
      const id = setInterval(tick, delayMs);
      return () => clearInterval(id);
    }
  }, [delayMs]);
}

/**
 * useInterval which is executed only when window/tab is active.
 * It will be also executed when user return to window
 *  - To disable this feature use ignoreMissedTicks parameter
 */
export function useActiveWindowInterval(
  callback: () => void,
  delayMs: number,
  ignoreMissedTicks: false | 'ignore-missed' = false
) {
  const lastCalledTs = useRef(0);

  useEffect(() => {
    if (delayMs < 100) {
      console.warn(
        'Warning: Don`t use useActiveWindowInterval with small delay'
      );
    }
  }, [delayMs]);

  const handleTick = useCallback(() => {
    if (document.hidden) {
      return;
    }
    // If we have fresh execution (1/2 of delay) - eg. user activated tab recently,
    // we will ignore event and wait for next one
    if (ignoreMissedTicks) {
      callback();
    } else if (lastCalledTs.current + delayMs / 2 < Date.now()) {
      lastCalledTs.current = Date.now();
      callback();
    }
  }, [callback, delayMs, ignoreMissedTicks]);

  useEffect(() => {
    if (ignoreMissedTicks) {
      return;
    }
    const visibilityChange = function () {
      if (document.hidden) {
        return;
      }
      if (lastCalledTs.current + delayMs < Date.now()) {
        handleTick();
      }
    };
    document.addEventListener('visibilitychange', visibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', visibilityChange);
  }, [handleTick, delayMs, ignoreMissedTicks]);

  return useInterval(handleTick, delayMs);
}

export function useTextCallbackThrottle<T extends string | number>(
  text: T,
  parentCallback: ((params: T) => void) | undefined,
  delayMs: number
) {
  const timeout = useRef<number | undefined>();
  const textRef = useRef(text);

  useEffect(() => {
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
    textRef.current = text;
    timeout.current = window.setTimeout(() => {
      if (parentCallback) {
        parentCallback(text);
      }
    }, delayMs);
    return () => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
    };
  }, [text, timeout, parentCallback, delayMs]);
}

/**
 * Custom Throttle (Delayed) hook which will be called on callback change,
 * but with throttle delay specified.
 *
 * Note that this custom throttle does not guarantee callback to be called
 * in defined ms interval, but instead, it waits until callback stops changing
 * and execute it afterwards.
 *
 *  @example
 *   const updateEstimate = useCallback(() => { ... }, [...]);
 *   useCallbackDelayedThrottle(updateEstimate, 1300);
 */
// export function useCallbackDelayedThrottle(
//   callback: () => void,
//   delayMs: number
// ) {
//   const timeout = useRef<number | undefined>();
//   const callbackRef = useRef(callback);

//   useEffect(() => {
//     if (timeout.current) {
//       clearTimeout(timeout.current);
//     }
//     callbackRef.current = callback;
//     timeout.current = window.setTimeout(() => {
//       callback();
//     }, delayMs);
//     return () => {
//       if (timeout.current) {
//         clearTimeout(timeout.current);
//       }
//     };
//   }, [callback, delayMs]);
// }

// export const useAsyncRedirect = () => {
//   const { history, location, match } = useReactRouter();
// }

// import { useState, useEffect } from 'react';

// function useResolution({ screenSize, onResizeTo }) {
//   useEffect(() => {
//     const resized = () => {
//       const { clientWidth } = window.document.documentElement;
//       const resizedToSize =
//         clientWidth <= 500
//           ? 'smallest'
//           : clientWidth <= 750
//           ? 'smaller'
//           : clientWidth <= 1000
//           ? 'small'
//           : 'normal';
//       console.log('[navbar] Resized to', resizedToSize);
//       if (screenSize !== resizedToSize) {
//         onResizeTo(resizedToSize);
//       }
//     };
//     window.addEventListener('resize', resized);
//     resized();
//     return () => {
//       window.removeEventListener('resize', resized);
//     };
//   }, [screenSize, onResizeTo]);
// }

// export const useWindowHeight = (onChanged?: () => void): number => {
//   const [height, setHeight] = useState(window.innerHeight);

//   useWindowEventListener('resize', () => {
//     const newHeight = window.innerHeight;
//     setHeight(newHeight);
//     if (onChanged && height !== newHeight) {
//       onChanged();
//     }
//   });

//   return height;
// };

/** Use window width which will be not accurate after resizing (delayed) */
// export function useDelayedWindowWidth(enabled: boolean) {
//   const [windowWidth, setWindowWidth] = useState(window.innerWidth);
//   useInterval(
//     () => {
//       const newWidth = window.innerWidth;
//       if (windowWidth !== newWidth) {
//         setWindowWidth(newWidth);
//       }
//     },
//     enabled ? 3000 : null
//   );
//   return { windowWidth };
// }

// export const useWindowWidth = (
//   onChanged?: () => void,
//   enabled = true
// ): number => {
//   const [width, setWidth] = useState(window.innerWidth);

//   useWindowEventListener(
//     'resize',
//     enabled
//       ? () => {
//           const newWidth = window.innerHeight;
//           setWidth(newWidth);
//           if (onChanged && width !== newWidth) {
//             onChanged();
//           }
//         }
//       : undefined
//   );

//   return width;
// };

export function useWebsocketEventListener<
  TYPE extends WebsocketSubscriptionType,
>(event: TYPE, callback: WebsocketCallback<TYPE>) {
  const callbackRef = useRef<typeof callback>();

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    const subscriptionId = listenToWebsocket(event, response => {
      if (callbackRef.current) {
        callbackRef.current(response);
      } else {
        console.warn(`Missing callback for websocket event - ${event}`);
      }
    });
    return () => {
      stopListenFromWebsocket(subscriptionId, event);
    };
  }, [event]);
}

/**
 * Handy feature for components appear animations
 * @example const width = useUpdateValueAfterFirstRender('0', '100px')
 */
export function useUpdateValueAfterFirstRender<T>(
  value: T,
  afterRenderValue: T,
  { ignoreInitial }: { ignoreInitial?: boolean }
) {
  const [initialized, setInitialized] = useState(false);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setInitialized(true);
  }, []);
  return ignoreInitial || initialized ? afterRenderValue : value;
}

/**
 * A custom hook that will capture and parse the query parameters from the
 * routers `search` value.
 */
export function useURLQuery(): URLSearchParams {
  const location = useLocation();
  const search = location.search;
  // const search = useSelector(state => state.router.location.search);
  const query = useMemo(() => new URLSearchParams(search), [search]);
  return query;
}

// export type RouterLocation<S = any> = Location<S>;

/**
 * Essentially is the same as the react-router useLocation except that we take advantage
 * of the fact that connected-router is Typescript native and provides good types for things.
 */
export function useRouterLocation() {
  const location = useLocation();
  // const location = useSelector(state => state.router.location);
  // const location = useLocation();
  return location;
}

/*
 * Make sure user will not do some action twice,
 * this will wait for promise resolve + specified minimal timeout.
 * @example const [onClick, disabled] = useDelayAfterEnablingOnClick(handleClick, 2000)
 */
// export function useDelayAfterEnablingOnClick(timeoutMs: number) {
//   const [waiting, setWaiting] = useState(false);
//   const [resolved, setResolved] = useState(false);

//   const callWrap = (fn: () => void | Promise<void>) => async () => {
//     setWaiting(true);
//     if (waiting) {
//       return;
//     }
//     await Promise.resolve(fn());
//     setResolved(true);
//   };

//   useEffect(() => {
//     if (waiting === false && resolved !== true) {
//       return;
//     }
//     // Set timeout to re-enable clickable action
//     const timeout = setTimeout(() => {
//       setWaiting(false);
//       setResolved(false);
//     }, timeoutMs);
//     return () => clearTimeout(timeout);
//   }, [timeoutMs, waiting, resolved]);

//   return { callWrap, enabled: waiting === false };
// }

/**
 * This selector is currently static - change is allowed only after website reload
 */
export const useSelectorActiveChain = () => {
  const activeChain = getActiveChainFromState();
  const { chainCurrency } = getCurrentStateChainConfig(activeChain);
  const nativeAssetTitle = getChainNativeAssetTitle(activeChain);
  const isNativeAssetSymbol = useCallback(
    (symbol: string) => chainCurrency === symbol,
    [chainCurrency]
  );
  return useMemo(
    () => ({
      activeChain,
      nativeAssetTitle,
      activeChainUrlCode: chainsMap[activeChain]?.shortCode,
      nativeAssetSymbol: chainCurrency,
      isNativeAssetSymbol,
      /** Chain is actively used and will use full API/websocket connection */
      chainIsListed: isChainListed(activeChain),
    }),
    [activeChain, nativeAssetTitle, chainCurrency, isNativeAssetSymbol]
  );
};

/**
 * Register outside clicks which are outside of passed "ref" element.
 * Inspired by https://medium.com/@kevinfelisilda/click-outside-element-event-using-react-hooks-2c540814b661
 * 
 * @example
 *  
 *  const ref = useRef<HTMLDivElement>(null);
    useOutsideClick(ref, isOpened, () => {
      setOpened(false);
    });
    return <div ref={ref}>...</div>
 */
export function useOutsideClick(
  ref: React.RefObject<null | HTMLElement>,
  enabled: boolean,
  callback: () => void
) {
  // COULD BE WRITTEN AS:
  // but this looks unused now?
  // useDocumentEventListener(
  //   'click',
  //   enabled
  //     ? event => {
  //         if (
  //           ref?.current &&
  //           !!event.target &&
  //           !ref.current.contains(event.target as Node)
  //         ) {
  //           callback();
  //         }
  //       }
  //     : undefined
  // );

  const handleClick = useCallback(
    (event: MouseEvent) => {
      if (
        ref?.current &&
        event?.target &&
        !ref.current.contains(event.target as Node)
      ) {
        callback();
      }
    },
    [callback, ref]
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }
    document.addEventListener('click', handleClick);
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [enabled, handleClick]);
}

// export const useTimeout = (onTimeout: () => void, delayMs: number) => {
//   useEffect(() => {
//     const timeout = window.setTimeout(onTimeout, delayMs);
//     return () => {
//       window.clearTimeout(timeout);
//     };
//   }, [delayMs, onTimeout]);
// };

/** https://developer.mozilla.org/en-US/docs/Web/API/Document/hidden */
// export function useDocumentVisibility() {
//   const [hidden, setHidden] = useState(document.hidden);

//   useEffect(() => {
//     const visibilityChange = () => {
//       setHidden(document.hidden);
//     };
//     document.addEventListener('visibilitychange', visibilityChange);
//     return () => {
//       document.removeEventListener('visibilitychange', visibilityChange);
//     };
//   }, []);

//   useInterval(() => {
//     if (!hidden) {
//       return;
//     }
//     // Only if it's hidden, we want to check if it's visible,
//     // it's safe to just keep things running if we are unsure
//     // but we cannot let those be off
//     if (!document.hidden) {
//       setHidden(document.hidden);
//     }
//   }, 3_000);

//   return {
//     documentIsHidden: hidden,
//   };
// }

/**
 * "Dangerous" as warning, because we use it with react-hooks/exhaustive-deps
 * to get initial value of referenced object.
 */
export const useDangerousRefResolution = (
  containerRef: React.RefObject<HTMLDivElement>
) => {
  const [resolution, setResolution] = useState<{
    width: number;
    height: number;
  }>();

  useEffect(() => {
    const resized = () => {
      if (containerRef.current) {
        setResolution({
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight,
        });
      }
    };
    window.addEventListener('resize', resized);
    return () => {
      window.removeEventListener('resize', resized);
    };
  }, [containerRef]);

  useActiveWindowInterval(
    () => {
      if (containerRef.current) {
        const { clientWidth, clientHeight } = containerRef.current;
        setResolution(newResolution =>
          newResolution?.height === clientHeight &&
          newResolution?.width === clientWidth
            ? // This should prevent infinite loop (ref stability)
              newResolution
            : { width: clientWidth, height: clientHeight }
        );
      }
    },
    // Initially we have to figure value quick as possible
    resolution ? 2000 : 100
  );

  return resolution;
};

/** Inteligent wheel scrolling even handler */
export const useWheelScroll = (handler: (up: boolean) => void) => {
  const cumulativeScrolled = useRef(0);
  const onWheel: WheelEventHandler<HTMLDivElement> = useCallback(
    event => {
      if (Math.abs(event.deltaY) > 50) {
        // If step was large enough - eg mouse with scroller which has a large "stepper"
        cumulativeScrolled.current = 0;
        handler(event.deltaY < 0);
        return;
      }

      const wasDown = cumulativeScrolled.current > 0;
      const isDown = event.deltaY > 0;
      if (isDown === wasDown) {
        cumulativeScrolled.current += event.deltaY;
      } else {
        // Reset and start over
        cumulativeScrolled.current = event.deltaY;
      }

      if (Math.abs(cumulativeScrolled.current) >= 100) {
        handler(cumulativeScrolled.current < 0);
        cumulativeScrolled.current = 0;
      }
    },
    [handler]
  );
  return { onWheel };
};
